import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AdCopyModel, AdCopyRequestOptions } from '@models/types'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['adCopies']

type AdCopyFilters = {
  campaign_id?: number,
  order_item_id?: number,
}

type UseAdCopiesOptions = {
  filters?: AdCopyFilters,
  performHttpRequests?: boolean,
  requestOptions?: AdCopyRequestOptions,
}

function useAdCopies(options: UseAdCopiesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    order_item_id: filterOrderItemId,
  } = filters

  const {
    updatedEntities: { adCopies: adCopiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { adCopies } = useSelector(reduxState => reduxState.entities)

  const filteredAdCopies = useMemo(() => {
    const filtered = Object.values(adCopies).filter((adCopy: AdCopyModel) => {
      const { campaign_id, order_item_id } = adCopy

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchOrderItemId = matchFilterNumber(order_item_id, filterOrderItemId)

      return matchCampaignId
      && matchOrderItemId
    })

    return sortArrayBy(filtered, 'asc')
  }, [adCopiesUpdatedAt, JSON.stringify(filters)])

  const filteredAdCopiesCount = filteredAdCopies.length
  const hasFilteredAdCopies = !!filteredAdCopiesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAdCopiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAdCopies } = useReduxAction(
    'adCopies',
    'loadAdCopies',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAdCopies,
    filteredAdCopiesCount,
    hasFilteredAdCopies,
    loading: loadingAdCopies,
  }
}

export default useAdCopies
